import {entry, objectFromEntries} from '@benchling/js-utils';

import {assertAndContinue} from 'Asserts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PickObject = Record<string, any>;

/**
 * Typesafe alternative to _.pick for many keys.
 *
 * Example:
 *
 * interface User {
 *   name: string;
 *   username: string;
 *   age: number;
 *   email: string;
 * }
 *
 * const user: User = {...};
 *
 * const userWithoutSensitiveData = pick(user, 'name', 'username');
 *
 * userWithoutSensitiveData.name;     // typechecks
 * userWithoutSensitiveData.username; // typechecks
 * userWithoutSensitiveData.age;      // does not typecheck
 * userWithoutSensitiveData.email;    // does not typecheck
 *
 * Also see:
 * - selectDefined
 * - pickDefined
 */
export default function pick<T extends PickObject, K extends keyof T>(
  object: T,
  ...keys: Array<K>
): Pick<T, K> {
  assertAndContinue(object != null, 'pick called on null or undefined!');
  assertAndContinue(typeof object === 'object', 'pick called on non-object!');
  assertAndContinue(
    keys.every((key) => typeof key === 'string'),
    'pick called with non-string key!'
  );

  return objectFromEntries(
    keys
      .filter((key) => object != null && typeof object === 'object' && object.hasOwnProperty(key))
      .map((key) => entry(key, object[key]))
  ) as Pick<T, K>;
}
